import {
  Control,
  Controller,
  ControllerRenderProps,
  FieldValues,
  get,
  Path,
  PathValue,
  useFormState,
} from "react-hook-form";
import { useEffect, useRef, useState } from "react";
import InformationCircleIcon from "../../assets/icons/info-circle.svg?react";
import { Field, Input, Label } from "@headlessui/react";

import clsx from "clsx";
import { roundNumberToFixed } from "@/lib/money.util";
import { useTranslateMessage } from "@/hooks/useTranslateMessage";

interface InputFormProps<FieldsType extends FieldValues> {
  title?: string;
  name: Path<FieldsType>;
  control: Control<FieldsType>;
  type?: React.HTMLInputTypeAttribute;
  defaultValue?: PathValue<FieldsType, Path<FieldsType>>;
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
  disabled?: boolean;
  tooltip?: string;
  rightElement?: React.ReactNode;
  tooltipLeft?: boolean;
  fixed?: number;
}

export const StyledInputStep = <FieldsType extends FieldValues>({
  title,
  control,
  name,
  type = "text",
  defaultValue,
  className,
  disabled,
  tooltip,
  rightElement,
  tooltipLeft,
  fixed = 2,
}: InputFormProps<FieldsType>) => {
  const translateMessage = useTranslateMessage();
  const { errors } = useFormState({ control });
  const errorMessage = get(errors, name + ".message") as unknown as string;

  const ref = useRef<HTMLInputElement>(null);
  const [focus, setFocus] = useState(false);

  const [internalInputValue, setInternalInputValue] = useState("");

  const getInput = (field: ControllerRenderProps<FieldsType, Path<FieldsType>>) => {
    // safe since this function is called every render
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      if (
        type == "number" &&
        (internalInputValue === "" || internalInputValue === "0") &&
        field.value != undefined &&
        field.value !== ""
      ) {
        setInternalInputValue(field.value.toString());
      }
    }, [field.value]);

    if (type === "checkbox") {
      return (
        <div className="flex flex-row items-center gap-2">
          <input type={type} id={name} className="peer size-4" {...field} />
          <label
            htmlFor={name}
            className={clsx([
              "pointer-events-none flex text-sm text-gray-300 transition-all duration-200 ease-in-out",
            ])}
          >
            {title}
          </label>
        </div>
      );
    }

    return (
      <Field
        className={clsx(
          "relative flex h-14 w-full items-center rounded-lg border px-4 hover:border-gray-300",
          {
            "border-blue-600": focus,
            "pointer-events-none": disabled,
            "bg-gray-200": disabled,
            "bg-white": !disabled,
          },
        )}
        onClick={() => {
          setFocus(true);
          if (ref.current) {
            ref.current.focus();
            const len = ref.current.value.length;
            ref.current.setSelectionRange(len, len);
          }
        }}
        onBlur={() => {
          setFocus(false);
          ref.current?.blur();
        }}
      >
        <Label
          className={clsx([
            "pointer-events-none absolute flex truncate overflow-hidden text-sm text-gray-500 transition-all duration-200 ease-in-out",
            {
              "text-xs": field.value?.length > 0,
              "-translate-y-3":
                field.value?.length > 0 || (field.value !== "" && field.value !== undefined),
            },
          ])}
        >
          {title}
        </Label>
        <Input
          {...field}
          ref={ref}
          id={name}
          // this hack enable to let the user type a decimal number with a comma or a dot
          onChange={(e) => {
            // First replace any comma with a period for parsing
            const formattedValue = e.target.value.replace(",", ".");

            // If not a valid number, keep the previous value
            if (isNaN(Number(formattedValue))) {
              setInternalInputValue(String(field.value) || "");
              return;
            }

            field.onChange(roundNumberToFixed(Number(formattedValue), fixed));

            setInternalInputValue(e.target.value);
          }}
          step={0.01}
          value={internalInputValue}
          type="text" // text to allow comma input
          inputMode="decimal" // Suggests decimal keyboard on mobile
          className={clsx("peer flex h-[80%] w-full bg-transparent focus:outline-hidden", {
            "border-red-500": errorMessage,
            "border-gray-300": !errorMessage,
            "translate-y-1.5": title && field.value !== undefined && field.value !== null,
          })}
        />

        {rightElement && <div className="flex flex-row items-center">{rightElement}</div>}
        {tooltip && (
          <div className="has-tooltip self-center">
            <span
              className={`tooltip pointer-events-none bottom-3/4 w-max max-w-64 ${tooltipLeft ? "-translate-x-3/4" : "-translate-x-1/4"} rounded-lg bg-blue-500 p-2 text-center text-sm font-semibold text-white shadow-lg`}
            >
              {tooltip}
            </span>
            <InformationCircleIcon className="size-6 text-blue-500" />
          </div>
        )}
      </Field>
    );
  };

  return (
    <div className={clsx("flex flex-col gap-2", className)}>
      <Controller
        control={control}
        name={name}
        render={({ field }) => getInput(field)}
        defaultValue={defaultValue}
      />
      {errorMessage !== "" && (
        <p className="text-xs text-red-500">{translateMessage(errorMessage)}</p>
      )}
    </div>
  );
};
